import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Calendar, Users, Plus, Sparkles } from "lucide-react";
import { api } from "../../lib/api";

function PageHeader({ icon: Icon, title, subtitle, cta }) {
  return (
    <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-2xl ashra-gradient-bg grid place-items-center shadow-sm">
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">{title}</h1>
          <p className="text-sm text-meta mt-0.5">{subtitle}</p>
        </div>
      </div>
      {cta && (
        <button className="rounded-full ashra-gradient-bg px-4 py-2 text-sm font-semibold text-white inline-flex items-center gap-1 shadow-sm hover:shadow-md transition focus-ring">
          <Plus className="h-3.5 w-3.5" /> {cta}
        </button>
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, title, hint, ctaLabel }) {
  return (
    <div className="rounded-2xl border border-dashed border-line bg-zinc-50/60 p-10 text-center" data-testid="empty-state">
      <div className="mx-auto mb-3 h-10 w-10 rounded-xl bg-white border border-line grid place-items-center"><Icon className="h-5 w-5 text-meta" /></div>
      <p className="font-display text-lg font-bold text-ink">{title}</p>
      <p className="mt-1 text-sm text-meta max-w-md mx-auto">{hint}</p>
      {ctaLabel && (
        <button className="mt-4 rounded-full ashra-gradient-bg px-4 py-2 text-sm font-semibold text-white inline-flex items-center gap-1 shadow-sm hover:shadow-md transition focus-ring">
          <Plus className="h-3.5 w-3.5" /> {ctaLabel}
        </button>
      )}
    </div>
  );
}

// =================== Memberships ===================
export function Memberships() {
  // Real membership tiers are stored against the workspace; the empty state
  // is honest until the creator publishes their first tier.
  const [tiers, setTiers] = useState([]);
  useEffect(() => {
    api.get("/memberships").then((r) => setTiers(r.data || [])).catch(() => setTiers([]));
  }, []);
  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="commerce-memberships">
      <PageHeader icon={Heart} title="Memberships" subtitle="Recurring access to your world" cta="New tier" />
      {tiers.length === 0 ? (
        <EmptyState icon={Heart} title="No tiers yet" hint="Create a recurring tier so paying members can join your Inner Circle. Recovery AI will keep them subscribed automatically." ctaLabel="Create your first tier" />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {tiers.map((t) => (
            <div key={t.name} className="surface p-6" data-testid={`tier-${t.name}`}>
              <div className={`h-10 w-10 rounded-2xl bg-gradient-to-br ${t.color || "from-violet-500 to-fuchsia-500"} grid place-items-center mb-4 shadow-sm`}>
                <Heart className="h-5 w-5 text-white" />
              </div>
              <p className="font-display text-2xl font-extrabold text-ink">{t.name}</p>
              <p className="font-display text-3xl font-extrabold text-ink mt-1">${t.price}<span className="text-sm font-medium text-meta">/{t.period || "month"}</span></p>
              <div className="grid grid-cols-2 gap-3 mt-5">
                <KPI label="Members" value={t.members || 0} tone="ink" />
                <KPI label="MRR" value={`$${(t.mrr || 0).toLocaleString()}`} tone="success" />
              </div>
              <button className="mt-5 w-full rounded-full ashra-gradient-bg py-2.5 text-sm font-semibold text-white shadow-sm hover:shadow-md transition focus-ring">Manage tier</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =================== Community ===================
export function Community() {
  const [channels, setChannels] = useState([]);
  useEffect(() => {
    api.get("/community/channels").then((r) => setChannels(r.data || [])).catch(() => setChannels([]));
  }, []);
  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="commerce-community">
      <PageHeader icon={MessageCircle} title="Community" subtitle="Private space for your buyers" cta="New channel" />
      {channels.length === 0 ? (
        <EmptyState icon={MessageCircle} title="No channels yet" hint="Spin up a private community space where buyers connect, share wins, and stay subscribed longer." ctaLabel="Create your first channel" />
      ) : (
        <div className="grid md:grid-cols-3 gap-4">
          {channels.map((c) => (
            <div key={c.name} className="surface p-5 hover:shadow-md transition" data-testid={`channel-${c.name}`}>
              <p className="font-mono font-bold text-ink text-lg">{c.name}</p>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <div><p className="text-[11px] uppercase text-meta font-semibold">Posts (7d)</p><p className="font-display text-xl font-bold text-ink">{c.posts || 0}</p></div>
                <div><p className="text-[11px] uppercase text-meta font-semibold">Members</p><p className="font-display text-xl font-bold text-ink">{c.members || 0}</p></div>
              </div>
              <p className="text-xs text-meta mt-3">Last activity: <span className="text-ink-soft font-semibold">{c.last || "—"}</span></p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =================== Bookings ===================
export function Bookings() {
  const [slots, setSlots] = useState([]);
  useEffect(() => {
    api.get("/bookings").then((r) => setSlots(r.data || [])).catch(() => setSlots([]));
  }, []);
  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="commerce-bookings">
      <PageHeader icon={Calendar} title="Bookings" subtitle="1:1 calls, audits, sessions" cta="New booking type" />
      {slots.length === 0 ? (
        <EmptyState icon={Calendar} title="No booking types yet" hint="Add a 1:1 call, audit, or VIP day. Closer AI will qualify leads before they reserve a slot — so your calendar fills with buyers, not freebie hunters." ctaLabel="Create your first booking type" />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {slots.map((s) => {
            const pct = s.capacity ? Math.round(((s.booked || 0) / s.capacity) * 100) : 0;
            return (
              <div key={s.title} className="surface p-6" data-testid={`booking-${s.title}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className={`h-10 w-10 rounded-2xl bg-gradient-to-br ${s.color || "from-violet-500 to-fuchsia-500"} grid place-items-center shadow-sm`}>
                    <Calendar className="h-5 w-5 text-white" />
                  </div>
                  <span className="pill pill-ai">{s.duration}min</span>
                </div>
                <p className="font-display text-lg font-bold text-ink">{s.title}</p>
                <p className="font-display text-2xl font-extrabold text-ink mt-1">{!s.price ? "Free" : `$${s.price}`}</p>
                <div className="mt-4">
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-meta">{s.booked || 0} of {s.capacity || 0} booked</span>
                    <span className="text-ink font-semibold">{pct}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                    <div className="h-full rounded-full ashra-gradient-bg" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =================== Customers ===================
export function Customers() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/leads?stage=paid").then((r) => setRows(r.data || [])).catch(() => {});
  }, []);
  const ltv = rows.reduce((s, r) => s + (r.revenue_potential || 0), 0);
  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="commerce-customers-page">
      <PageHeader icon={Users} title="Customers" subtitle="Everyone who's bought, subscribed, or attended" />
      {rows.length > 0 && (
        <div className="rounded-xl border border-violet-200 bg-ai-soft text-ai px-4 py-3 text-sm inline-flex items-start gap-2 mb-5">
          <Sparkles className="h-4 w-4 mt-0.5 shrink-0" />
          <span className="text-ink-soft">{rows.length} paying customer{rows.length === 1 ? "" : "s"}. See <Link to="/capture?tab=opportunities" className="text-ai font-semibold hover:underline">Capture → Opportunities</Link> for upsell candidates.</span>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <KPI label="Paying customers" value={rows.length} tone="ink" />
        <KPI label="Total LTV" value={`$${Math.round(ltv).toLocaleString()}`} tone="success" />
        <KPI label="Avg order" value={`$${rows.length ? Math.round(ltv / rows.length) : 0}`} tone="ai" />
      </div>

      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-meta uppercase tracking-wider font-semibold bg-zinc-50">
            <tr><th className="px-4 py-3">Customer</th><th>Source</th><th>LTV</th><th>Last activity</th></tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.slice(0, 20).map((r) => (
              <tr key={r.id} className="hover:bg-zinc-50/60">
                <td className="px-4 py-3">
                  <p className="font-semibold text-ink">{r.name}</p>
                  <p className="text-xs text-meta">{r.handle}</p>
                </td>
                <td className="text-meta capitalize">{r.source}</td>
                <td className="text-success font-bold">${Math.round(r.revenue_potential || 0).toLocaleString()}</td>
                <td className="text-meta text-xs">{(r.last_activity || "").slice(0, 10)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td className="px-4 py-6 text-meta italic" colSpan={4}>No paying customers yet. The first conversion will appear here.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="mt-4 text-xs text-meta-soft">Tip: For lead-stage pipeline see <Link to="/capture?tab=leads" className="text-ai font-semibold hover:underline">Capture → Leads</Link> in AI Revenue OS.</p>
    </div>
  );
}

function KPI({ label, value, tone }) {
  const toneCls = tone === "success" ? "text-success" : tone === "ai" ? "text-ai" : "text-ink";
  return (
    <div className="rounded-2xl bg-zinc-50 border border-line p-4">
      <p className="text-[11px] uppercase tracking-wider text-meta font-semibold">{label}</p>
      <p className={`mt-1 font-display text-2xl font-extrabold ${toneCls}`}>{value}</p>
    </div>
  );
}
